/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {Draggable} from 'react-beautiful-dnd';
import ListItem from '@material-ui/core/ListItem';
import makeStyles from '@material-ui/core/styles/makeStyles';
import classnames from 'classnames';
import {toggleThemeStyle} from '../../theme/helpers';
import {dlsColor} from '../../theme/theme';

/**
 * Defines the prop type style definitions for the DraggableListItem component, which are
 * as follows:
 *
 * itemBackground?: string: Background for item
 * itemDragBackground: string: Background for item while drag
 * itemMargin?: string: Margin for item
 * itemClassName?: string: Class name for item
 * draggingItemClassName?: string: Class name for item while drag
 * noDataTxtMargin?: string: Margin for string when there is no data
 */
export interface DraggableListStyleProps{
  itemBackground?: string,
  itemDragBackground?: string,
  itemMargin?: string | number,
  itemClassName?: string,
  draggingItemClassName?: string,
  noDataTxtMargin?: string
}

/**
 * Defines the prop type style definitions for the DraggableItem which are as follows,
 *
 * id: string, Unique identifier for the individual item
 * primaryText: string, The primaryText that needs to show in list items
 */
export interface DraggableItem {
  id: string,
  primaryText: string,
  [key: string]: any
};

// defines the DraggableItems type
export type DraggableItems = DraggableItem[];

/**
 * Defines the prop type definitions for the DraggableListItem component, which are
 * as follows:
 *
 * item?: DraggableItem: The item that need to be showed.
 * renderContent: Function: The function which will render the individual item in a list.
 * isDragDisabled?: string: Indicated the state of drag.
 * index?: number: index for the item.
 */
export interface DraggableListItemProps extends DraggableListStyleProps {
  item: DraggableItem,
  index: number,
  isDragDisabled?: boolean,
  handleOnly?: boolean,
  renderContent?: Function
};

// defines the style definitions for DraggableListItem
const useStyles = makeStyles(theme => ({
  item : {
    alignItems : 'stretch',
    background : ({itemBackground}: DraggableListStyleProps) => itemBackground || dlsColor('white'),
    boxSizing  : 'border-box',
    display    : 'block',
    margin     : ({itemMargin}) => itemMargin ?? 12,
    padding    : toggleThemeStyle('revert-rule', '0px'),
    width      : '100%'
  },
  draggingListItem : {
    alignItems : 'stretch',
    background : ({itemDragBackground}) => itemDragBackground || dlsColor('grey4'),
    boxSizing  : 'border-box',
    display    : 'block',
    margin     : ({itemMargin}) => itemMargin ?? 12,
    padding    : 0,
    width      : '100%'
  }
}));

/**
 * Defines the DraggableListItem component. This component is reusable to show the individual item of
 * list of component or items. This can render the component as list item using the renderContent function.
 *
 * ```typescript
 *
 *  import {DraggableListItem} from '@ion/dls/src/draggable';
 *
 *   <DraggableListItem item={item}
 *      isDragDisabled={isDragDisabled}
 *      renderContent={renderContent}
 *      index={index}
 *      key={item.id}
 *      {...props} />
 *
 * ```
 */
const DraggableListItem = ({
  item,
  renderContent,
  index,
  isDragDisabled = false,
  handleOnly = false,
  ...props
}: DraggableListItemProps) => {
  const classes = useStyles(props);
  const {
    itemClassName,
    draggingItemClassName
  } = props;

  return (
    <Draggable isDragDisabled={isDragDisabled}
      draggableId={item.id?.toString()}
      index={index}>
      {(provided, snapshot) => (
        <ListItem ref={provided.innerRef}
          {...provided.draggableProps}
          {...(handleOnly ? undefined : provided.dragHandleProps)}
          className={classnames(
            snapshot.isDragging ? classes.draggingListItem : classes.item,
            snapshot.isDragging ? draggingItemClassName : itemClassName
          )}>
          {renderContent?.(item, index, handleOnly ? provided.dragHandleProps : undefined) || item.primaryText}
        </ListItem>
      )}
    </Draggable>
  );
};

export default DraggableListItem;
